import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type {
  AccountService,
  IAccountServiceUser,
  SaveService,
  StoreIdCookieInformationService,
  ZipCodeCookieInformationService
} from '@inter-ikea-kompis/services';
import { useServiceSettings } from '../serviceSettings';
import {
  createAccountService,
  createSaveService,
  createStoreIdCookieInformationService,
  createZipCodeService,
  getTokenExpiry,
  isUserTokenExpired
} from './utils';
import type { PlannerAccountService } from './PlannerAccountService';
import { isKiosk } from '@inter-ikea-storageone/shared/utils/platform';
import type { AnalyticsViewSource } from '@inter-ikea-storageone/shared/analytics';
import {
  reportAccountLogin,
  reportAccountLogout,
  reportUserLoggedIn
} from '@app/analytics/reporters';
import { createStore, useStore } from 'zustand';
import type { IStorageOneDexfSettings } from '@inter-ikea-storageone/shared/types';
import type { DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums';
import { getQuery } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { subscribeWithEqualityFn } from '@inter-ikea-storageone/shared/platform/utils/zustand-utils/subscribeWithEqualityFn';

type LoginOptions = Parameters<AccountService['logIn']>[0] & {
  state: 'openDesign' | 'saveDesign' | 'saveAsDesign' | 'unspecified';
  redirectUri: string;
};
type InitiateLogin = (
  source: AnalyticsViewSource,
  loginOptions: LoginOptions
) => Promise<IAccountServiceUser | null>;

type FetchUser = () => Promise<IAccountServiceUser | null>;
type FetchUserWithLoginAsBackup = (
  source: AnalyticsViewSource,
  loginOptions: LoginOptions
) => Promise<IAccountServiceUser | null>;

type Logout = (source: AnalyticsViewSource.hamburgerMenu) => Promise<void>;

type UserAuthUpdateActionType =
  | 'getAccountService:create'
  | 'updateUser:clear'
  | 'updateUser:set'
  | 'silentAuth:start'
  | 'silentAuth:error'
  | 'silentAuth:complete'
  | 'silentAuth:storePromise'
  | 'fetchUser:start'
  | 'fetchUser:error'
  | 'fetchUser:complete'
  | 'updateTokenStatus';

type UserAuthUpdateActionId = `${UserAuthUpdateActionType}:${number}:${number}`;

interface UserAuthLastUpdateAction {
  id: UserAuthUpdateActionId;
  type: UserAuthUpdateActionType;
  at: string;
}

interface UserAuthState {
  dexfSettings: IStorageOneDexfSettings;
  filterSegment: DexfSettingsFilterSegmentEnum;
  accountService: PlannerAccountService | null;
  user: IAccountServiceUser | null;
  tokenExpiry: Date | null;
  isTokenExpired: boolean;
  lastUpdateAction: UserAuthLastUpdateAction | null;
  getAccountService: () => PlannerAccountService;
  silentAuthAttempted: boolean;
  silentAuthAttemptInProgress: boolean;
  silentAuthErr: unknown;
  silentAuthUserPromise: Promise<IAccountServiceUser | null> | null;
  fetchUserAttemptInProgress: boolean;
  fetchUserErr: unknown;
  getSilentAuthUserPromise: () => Promise<IAccountServiceUser | null>;
  initiateLogin: InitiateLogin;
  logout: Logout;
  fetchUser: FetchUser;
  fetchUserWithLoginAsBackup: FetchUserWithLoginAsBackup;
  updateTokenStatus: () => void;
}

const createUserAuthStore = ({
  dexfSettings,
  filterSegment,
  disallowLogin,
  enableAuthDebug = false
}: {
  dexfSettings: IStorageOneDexfSettings;
  filterSegment: DexfSettingsFilterSegmentEnum;
  disallowLogin: boolean;
  enableAuthDebug: boolean;
}) => {
  const store = createStore<UserAuthState>((set, get) => {
    let updateActionCount = 0;

    function setStateWithAction(
      actionType: UserAuthUpdateActionType,
      partial: Partial<UserAuthState> | ((state: UserAuthState) => Partial<UserAuthState>)
    ) {
      const actionId: UserAuthUpdateActionId = `${actionType}:${Date.now()}:${++updateActionCount}`;
      const lastUpdateAction = {
        id: actionId,
        type: actionType,
        at: new Date().toISOString()
      };

      set(state => ({
        ...(typeof partial === 'function' ? partial(state) : partial),
        lastUpdateAction
      }));
    }

    function getAccountService() {
      const state = get();
      const { dexfSettings, filterSegment } = state;
      if (state.accountService) {
        return state.accountService;
      }
      const accountService = createAccountService(dexfSettings, filterSegment);
      setStateWithAction('getAccountService:create', {
        accountService
      });
      return accountService;
    }

    function updateUser(user: IAccountServiceUser | null) {
      if (!user) {
        setStateWithAction('updateUser:clear', {
          user: null,
          tokenExpiry: null,
          isTokenExpired: false
        });
        return;
      }
      setStateWithAction('updateUser:set', {
        user: user,
        tokenExpiry: getTokenExpiry(user),
        isTokenExpired: isUserTokenExpired(user)
      });
    }
    async function getSilentAuthUserPromise() {
      if (disallowLogin) {
        return null;
      }
      const state = get();
      if (state.silentAuthUserPromise && !state.isTokenExpired) {
        return await state.silentAuthUserPromise;
      }
      const accountService = state.getAccountService();
      setStateWithAction('silentAuth:start', {
        silentAuthAttempted: true,
        silentAuthAttemptInProgress: true
      });

      const createSilentAuthPromise = async () => {
        try {
          const silentlyAuthenticatedUser = await accountService.getUser({
            useSilentAuthentication: true
          });
          if (silentlyAuthenticatedUser) {
            reportUserLoggedIn({ loggedIn: true }).catch(error => {
              // eslint-disable-next-line no-console
              console.error(error);
            });
            updateUser(silentlyAuthenticatedUser);
            return silentlyAuthenticatedUser;
          }
        } catch (silentAuthErr) {
          setStateWithAction('silentAuth:error', { silentAuthErr });
        } finally {
          setStateWithAction('silentAuth:complete', {
            silentAuthAttemptInProgress: false
          });
        }
        return null;
      };
      const silentAuthUserPromise = createSilentAuthPromise();
      setStateWithAction('silentAuth:storePromise', { silentAuthUserPromise });
      return await silentAuthUserPromise;
    }

    const initiateLogin: InitiateLogin = async (source, loginOptions) => {
      if (disallowLogin) {
        return null;
      }
      reportAccountLogin({ source }).catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
      const accountService = get().getAccountService();
      return (await accountService.logIn(loginOptions)) ?? null;
    };
    const logout: Logout = async source => {
      reportAccountLogout({
        source
      }).catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
      });

      updateUser(null);
      await get().getAccountService().logOut();
    };
    const fetchUser: FetchUser = async () => {
      if (disallowLogin) {
        return null;
      }
      try {
        await get().getSilentAuthUserPromise();
      } catch {
        // ignore
      }
      setStateWithAction('fetchUser:start', {
        fetchUserAttemptInProgress: true,
        fetchUserErr: null
      });
      try {
        const user = await get().getAccountService().getUser();
        updateUser(user);
        return user;
      } catch (fetchUserErr) {
        setStateWithAction('fetchUser:error', {
          user: null,
          tokenExpiry: null,
          isTokenExpired: false,
          fetchUserErr
        });
      } finally {
        setStateWithAction('fetchUser:complete', {
          fetchUserAttemptInProgress: false
        });
      }
      return null;
    };
    const fetchUserWithLoginAsBackup: FetchUserWithLoginAsBackup = async (source, loginOptions) => {
      if (disallowLogin) {
        return null;
      }
      const user = await get().fetchUser();

      if (user) {
        return user;
      }
      // This might mean the token has expired. Prompt the user to log in again.
      const loggedInUser = await get().initiateLogin(source, loginOptions ?? undefined);
      return loggedInUser;
    };
    function updateTokenStatus() {
      setStateWithAction('updateTokenStatus', ({ user }) => {
        return {
          tokenExpiry: user ? getTokenExpiry(user) : null,
          isTokenExpired: user ? isUserTokenExpired(user) : false
        };
      });
    }

    return {
      dexfSettings,
      filterSegment,
      accountService: null,
      user: null,
      tokenExpiry: null,
      isTokenExpired: false,
      lastUpdateAction: null,
      silentAuthAttempted: false,
      silentAuthAttemptInProgress: false,
      silentAuthUserPromise: null,
      silentAuthErr: null,
      fetchUserAttemptInProgress: false,
      fetchUserErr: null,
      getAccountService,
      getSilentAuthUserPromise,
      fetchUser,
      fetchUserWithLoginAsBackup,
      initiateLogin,
      logout,
      updateTokenStatus
    };
  });
  // update token status to expired when applicable
  subscribeWithEqualityFn<UserAuthState, number | null>(
    tokenExpiryTime => {
      if (tokenExpiryTime == null) {
        return;
      }
      const millisecondsRemaining = tokenExpiryTime - Date.now();
      setTimeout(
        () => {
          store.getState().updateTokenStatus();
        },
        Math.max(0, millisecondsRemaining)
      );
    },
    store,
    state => state.tokenExpiry?.getTime() ?? null,
    Object.is
  );
  if (enableAuthDebug) {
    subscribeWithEqualityFn<UserAuthState, UserAuthLastUpdateAction | null>(
      lastUpdateAction => {
        if (lastUpdateAction == null) {
          return;
        }
        const authState = store.getState();
        // eslint-disable-next-line no-console
        console.log(lastUpdateAction, { authState });
      },
      store,
      state => state.lastUpdateAction,
      Object.is
    );
  }
  return store;
};

type ServicesContextValue = {
  user: IAccountServiceUser | null;
  initiateLogin: InitiateLogin;
  logout: Logout;
  fetchUser: FetchUser;
  fetchUserWithLoginAsBackup: FetchUserWithLoginAsBackup;
  getSaveService: () => SaveService;
  getSilentAuthUserPromise: () => Promise<IAccountServiceUser | null>;
  zipCodeService: ZipCodeCookieInformationService;
  storeService: StoreIdCookieInformationService;
};

export const ServicesContext = createContext<ServicesContextValue>({
  user: null,
  logout: () => {
    throw new Error('ServicesProvider is missing');
  },
  initiateLogin: async () => {
    throw new Error('ServicesProvider is missing');
  },
  fetchUser: async () => {
    throw new Error('ServicesProvider is missing');
  },
  fetchUserWithLoginAsBackup: async () => {
    throw new Error('ServicesProvider is missing');
  },
  getSaveService: () => {
    throw new Error('ServicesProvider is missing');
  },
  getSilentAuthUserPromise: () => Promise.resolve(null),
  zipCodeService: {} as ZipCodeCookieInformationService,
  storeService: {} as StoreIdCookieInformationService
});

export const useServices = () => useContext(ServicesContext);

export const InitSilentAuthEffect = () => {
  const { getSilentAuthUserPromise } = useServices();
  useEffect(() => {
    getSilentAuthUserPromise();
  }, [getSilentAuthUserPromise]);
  return null;
};

export const ServicesProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    dexfSettings,
    kompisSettings: { filterSegment }
  } = useServiceSettings();

  const disallowLogin = isKiosk();
  const userAuthStore = useMemo(
    () =>
      createUserAuthStore({
        dexfSettings,
        filterSegment,
        disallowLogin,
        enableAuthDebug: getQuery().get('enableAuthDebug') === 'true'
      }),
    [dexfSettings, disallowLogin, filterSegment]
  );
  const user = useStore(userAuthStore, state => state.user);
  const getSilentAuthUserPromise = useStore(userAuthStore, state => state.getSilentAuthUserPromise);
  const initiateLogin = useStore(userAuthStore, state => state.initiateLogin);
  const logout = useStore(userAuthStore, state => state.logout);
  const fetchUser = useStore(userAuthStore, state => state.fetchUser);
  const fetchUserWithLoginAsBackup = useStore(
    userAuthStore,
    state => state.fetchUserWithLoginAsBackup
  );

  const saveServiceRef = useRef<SaveService | null>(null);
  const getSaveService = useCallback(() => {
    if (!saveServiceRef.current) {
      saveServiceRef.current = createSaveService(dexfSettings, filterSegment);
    }
    return saveServiceRef.current;
  }, [dexfSettings, filterSegment]);
  const zipCodeService = useMemo(() => createZipCodeService(dexfSettings), [dexfSettings]);
  const storeService = useMemo(
    () => createStoreIdCookieInformationService(dexfSettings),
    [dexfSettings]
  );
  const context = useMemo(
    (): ServicesContextValue => ({
      initiateLogin,
      logout,
      fetchUser,
      fetchUserWithLoginAsBackup,
      getSaveService,
      getSilentAuthUserPromise,
      zipCodeService,
      storeService,
      user
    }),
    [
      initiateLogin,
      logout,
      fetchUser,
      fetchUserWithLoginAsBackup,
      getSaveService,
      getSilentAuthUserPromise,
      zipCodeService,
      storeService,
      user
    ]
  );
  return (
    <ServicesContext.Provider value={context}>
      <InitSilentAuthEffect />
      {children}
    </ServicesContext.Provider>
  );
};
